import { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Send, CheckCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

gsap.registerPlugin(ScrollTrigger);

export default function ContactCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { y: '10vh', opacity: 0, scale: 0.98 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: cardRef.current,
            start: 'top 75%',
            end: 'top 45%',
            scrub: 0.4,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative bg-synapse-black py-24 lg:py-32"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 40%, rgba(243,195,86,0.08), rgba(7,8,10,1) 55%)',
        }}
      />

      <div className="relative z-10 max-w-xl mx-auto px-6 lg:px-12">
        <div
          ref={cardRef}
          className="bg-synapse-graphite/60 border border-white/10 rounded-3xl p-8 lg:p-10 will-change-transform"
        >
          {!isSubmitted ? (
            <>
              <div className="text-center mb-8">
                <p className="font-mono text-[10px] tracking-[0.2em] text-white/35 uppercase mb-3">
                  (aligned contact surface)
                </p>
                <h2 className="font-heading font-semibold text-2xl lg:text-3xl text-white tracking-tight mb-3">
                  Keep the surface aligned
                </h2>
                <p className="text-sm text-synapse-gray-300">
                  Share the context and the next pass can continue from the same language.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs font-mono tracking-wider text-white/50 uppercase">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-synapse-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-synapse-gold/50 focus:ring-synapse-gold/20"
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs font-mono tracking-wider text-white/50 uppercase">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-synapse-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-synapse-gold/50 focus:ring-synapse-gold/20"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-xs font-mono tracking-wider text-white/50 uppercase">
                    Company
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="bg-synapse-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-synapse-gold/50 focus:ring-synapse-gold/20"
                    placeholder="Your company"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs font-mono tracking-wider text-white/50 uppercase">
                    Context
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="bg-synapse-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-synapse-gold/50 focus:ring-synapse-gold/20 resize-none"
                    placeholder="What should the next alignment pass protect?"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-synapse-gold text-synapse-black hover:bg-synapse-amber font-medium py-3 rounded-xl btn-hover"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send context
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-synapse-gold mx-auto mb-6" />
              <h3 className="font-heading font-semibold text-xl text-white mb-3">
                Context recorded
              </h3>
              <p className="text-sm text-synapse-gray-300">
                The next surface pass can now continue from the same rules.
              </p>
            </div>
          )}
        </div>

        <footer className="mt-16 pt-8 border-t border-white/5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="font-heading font-semibold text-lg text-white">
              Synaptic Clone
            </div>
            <div className="flex items-center gap-6">
              <button className="font-mono text-xs tracking-wider text-white/40 hover:text-white/60 transition-colors">
                Privacy
              </button>
              <button className="font-mono text-xs tracking-wider text-white/40 hover:text-white/60 transition-colors">
                Terms
              </button>
            </div>
            <div className="font-mono text-xs tracking-wider text-white/30">
              © 2026 Synaptic Clone
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
}
